/**
 * Virtual clock — makes the REAL sandbox runtime deterministic for tests.
 *
 * The real runtime (`@gotti/test-utils` `initializer` → `MockEnvironment`) drives
 * everything off real time: the rAF game loop, the `setTimeout`-based transport
 * latency (`initializer.ts:264`), and the async join round-trip. That makes it
 * nondeterministic + real-time-slow, which is why tests historically used the
 * synchronous `createTestHarness` mirror instead.
 *
 * This module virtualizes ALL time so the real runtime can be driven by manual
 * fixed-step ticks AND stay byte-deterministic:
 *   - `setTimeout` / `setInterval` / `requestAnimationFrame` queue callbacks at a
 *     virtual due-time instead of firing on the wall clock.
 *   - `Date.now` / `performance.now` read the virtual `now`.
 *   - `Math.random` is the same mulberry32 PRNG the determinism shim uses.
 *   - `advance(ms)` fires every due callback in (due, seq) order, flushing
 *     microtasks between each firing (so an `await` chained off a fired timer
 *     resolves before the next timer), then moves `now` forward.
 *
 * Pure JS, no Node builtins (bundles into the browser app). A separate, simpler
 * shim than `installDeterministicGlobals` (which uses a monotonic-per-call clock
 * suited to the synchronous harness); the real-runtime path installs THIS one.
 */

type TimerKind = 'timeout' | 'interval' | 'raf';

/**
 * Replace `performance.now`, tolerating environments where it is a non-writable
 * own/prototype property (some Node loader setups — a bare assignment throws
 * "Cannot assign to read only property 'now'"). defineProperty on the instance
 * shadows a read-only prototype slot; if even that is forbidden, warn and leave
 * the real clock in place rather than killing the runner setup.
 *
 * Exported so `testRuntime.ts`'s `installDeterministicGlobals` / `restoreGlobals`
 * (the OTHER clock-install path — the synchronous-harness shim, as opposed to
 * this module's advance-able real-runtime clock) route through the SAME
 * tolerant setter instead of bare-assigning `performance.now` a second time —
 * one install path, shared robustness.
 */
export function setPerformanceNow(fn: () => number): void {
  try {
    performance.now = fn;
  } catch {
    try {
      Object.defineProperty(performance, 'now', { value: fn, configurable: true, writable: true });
    } catch (err) {
      console.warn('[virtualClock] could not virtualize performance.now — leaving real clock', err);
    }
  }
}

interface VirtualTimer {
  id: number;
  due: number;
  seq: number;
  kind: TimerKind;
  cb: (...args: unknown[]) => void;
  args: unknown[];
  /** interval period (ms) for setInterval; undefined otherwise. */
  interval?: number;
}

interface SavedGlobals {
  setTimeout: typeof setTimeout;
  clearTimeout: typeof clearTimeout;
  setInterval: typeof setInterval;
  clearInterval: typeof clearInterval;
  setImmediate: unknown;
  clearImmediate: unknown;
  requestAnimationFrame: typeof globalThis.requestAnimationFrame | undefined;
  cancelAnimationFrame: typeof globalThis.cancelAnimationFrame | undefined;
  winRaf: unknown;
  winCancelRaf: unknown;
  winSetImmediate: unknown;
  mathRandom: () => number;
  dateNow: () => number;
  perfNow: (() => number) | undefined;
  /** The real process.hrtime (node), saved so restoreVirtualClock puts it back. */
  processHrtime: ((time?: [number, number]) => [number, number]) | undefined;
}

/** The window object when it is a DISTINCT global from globalThis (it usually is
 *  not in modern browsers, but ClientProcess reads `window.requestAnimationFrame`
 *  explicitly, so we mirror the overrides there to be safe). Null in Node. */
const distinctWindow: Record<string, unknown> | null =
  typeof window !== 'undefined' && (window as unknown) !== (globalThis as unknown)
    ? (window as unknown as Record<string, unknown>)
    : null;

/** Standard rAF cadence (60fps). The game loop self-throttles to its own
 *  timeStep, so firing rAF every ~16.67ms of virtual time reproduces the real
 *  "rAF 60fps, game updates at its configured rate" behaviour deterministically. */
const FRAME_MS = 1000 / 60;

/** `setImmediate` advances virtual time by this tiny amount instead of 0. The
 *  real ServerProcess loop BUSY-WAITS toward its tick boundary by rescheduling
 *  `setImmediate(gameLoop)` (ServerProcess.ts:486) — under a real clock each
 *  re-fire lets a sliver of wall time pass, so it eventually reaches the boundary.
 *  A pure zero-delay virtual setImmediate would re-fire at the SAME instant
 *  forever (the runaway guard trips). Advancing 1ms per fire makes the busy-wait
 *  converge to the boundary deterministically (≤ tick-length fires), like rAF's
 *  FRAME_MS does for the client loop. */
const SET_IMMEDIATE_MS = 1;

/** Safety cap: a timer that reschedules itself at the SAME virtual instant (zero
 *  delay) would spin forever. We count consecutive firings at an unchanged `now`
 *  — a real runaway keeps `now` fixed, while legit timers across advancing time
 *  reset the counter — so this triggers fast on a spin and never on real work. No
 *  legitimate case fires this many timers at one exact virtual instant. */
const MAX_FIRINGS_AT_INSTANT = 5_000;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let installed = false;
/**
 * Which install of the clock is current. Each `installVirtualClock` makes a new one — a later install supersedes an
 * earlier one that was never restored — and hands it out as a `VirtualClockToken`.
 */
let generation = 0;
/**
 * Which boot is current: `resetVirtualClock` — every game boot calls it — starts a new one, within the same install.
 * A game ticks with the boot it was booted in (`currentVirtualClock` right after the reset), so a game whose test timed
 * out while it went on cannot drive the next test's clock in the same run (the install is still the run's).
 */
let epoch = 0;
let now = 0;
let clockStart = 0;
let nextId = 1;
let seq = 0;
const pending = new Map<number, VirtualTimer>();
let saved: SavedGlobals | null = null;

function schedule(kind: TimerKind, cb: (...a: unknown[]) => void, delay: number, args: unknown[], interval?: number): number {
  const id = nextId++;
  // Clamp negatives to 0; rAF ignores its delay arg and uses FRAME_MS.
  const wait = kind === 'raf' ? FRAME_MS : Math.max(0, Number(delay) || 0);
  pending.set(id, { id, due: now + wait, seq: seq++, kind, cb, args, interval });
  return id;
}

/** The earliest pending timer with due ≤ target, by (due, seq). Null if none. */
function earliest(target: number): VirtualTimer | null {
  let best: VirtualTimer | null = null;
  for (const t of pending.values()) {
    if (t.due > target) continue;
    if (!best || t.due < best.due || (t.due === best.due && t.seq < best.seq)) best = t;
  }
  return best;
}

/** Number of microtask-queue yields per drain — enough to settle the
 *  promise-chain depths the runtime + transport use, deterministic, and far
 *  cheaper than a real macrotask turn (which is ~1ms each in Node and would
 *  dominate wall-clock when a test fires thousands of timers). */
const MICROTASK_DRAIN_YIELDS = 8;

/** Hand control back to the microtask queue so Promises chained off a fired timer
 *  (e.g. the transport's async delivery / the join round-trip) resolve before the
 *  next virtual timer fires. Yields N times so a few-deep `await` chain fully
 *  settles. Deterministic (microtask order is FIFO). */
async function flushMicrotasks(): Promise<void> {
  for (let i = 0; i < MICROTASK_DRAIN_YIELDS; i++) await Promise.resolve();
}

/**
 * One install of the virtual clock, as `installVirtualClock` returns it: what a run hands back to `restoreVirtualClock`
 * and `advance`. A later install supersedes it, and from then on both refuse it. A token from `currentVirtualClock`
 * also names the BOOT it was taken in (`epoch`): `advance` refuses it once a later boot reset the clock, while restore
 * reads only the install.
 */
export interface VirtualClockToken {
  readonly generation: number;
  /** The boot this token's game runs in (see `resetVirtualClock`); absent from an install's own token. */
  readonly epoch?: number;
}

/**
 * Install the virtual clock + seeded RNG, and return the token of this install.
 *
 * A clock still installed — by a run that never restored it: one whose test timed out while its work went on — is
 * superseded, not shared: virtual time restarts at 0 with this seed, every pending timer of the earlier install is
 * dropped, and the earlier token goes stale, so the earlier run's teardown (`restoreVirtualClock(token)`) and ticks
 * (`advance(ms, token)`) can no longer uninstall or drive this run's clock. The real globals stay the ones the first
 * install saved.
 */
export function installVirtualClock(seed: { random: number; clockStart: number }): VirtualClockToken {
  if (installed) {
    generation += 1;
    now = 0;
    clockStart = seed.clockStart;
    nextId = 1;
    seq = 0;
    pending.clear();
    Math.random = mulberry32(seed.random);
    return { generation };
  }
  installed = true;
  generation += 1;
  now = 0;
  clockStart = seed.clockStart;
  nextId = 1;
  seq = 0;
  pending.clear();

  const g = globalThis as unknown as Record<string, unknown>;
  saved = {
    setTimeout: globalThis.setTimeout,
    clearTimeout: globalThis.clearTimeout,
    setInterval: globalThis.setInterval,
    clearInterval: globalThis.clearInterval,
    setImmediate: g['setImmediate'],
    clearImmediate: g['clearImmediate'],
    requestAnimationFrame: (g['requestAnimationFrame'] as SavedGlobals['requestAnimationFrame']) ?? undefined,
    cancelAnimationFrame: (g['cancelAnimationFrame'] as SavedGlobals['cancelAnimationFrame']) ?? undefined,
    winRaf: distinctWindow?.['requestAnimationFrame'],
    winCancelRaf: distinctWindow?.['cancelAnimationFrame'],
    winSetImmediate: distinctWindow?.['setImmediate'],
    mathRandom: Math.random,
    dateNow: Date.now,
    perfNow: typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now.bind(performance)
      : undefined,
    processHrtime:
      typeof process !== 'undefined' && typeof process.hrtime === 'function'
        ? process.hrtime
        : undefined,
  };

  const next = mulberry32(seed.random);
  Math.random = next;
  Date.now = () => clockStart + Math.floor(now);
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    setPerformanceNow(() => now);
  }
  // process.hrtime PARITY (node): ServerProcess's tick gate reads
  // process.hrtime() (ServerProcess.getNano). In the browser there is no real
  // hrtime — the test-utils nodejs mock installs a performance.now-backed shim,
  // so the gate follows this virtual clock automatically. On node the REAL
  // hrtime would keep reading REAL time, so the gate never opens under
  // advance() and server ticks silently freeze (snake movement/tick parity
  // gap). Derive hrtime from the virtual `now` so both runtimes read the same
  // clock. Restored in restoreVirtualClock().
  if (saved.processHrtime) {
    const hrtimeShim = (time?: [number, number]): [number, number] => {
      const ms = now;
      const seconds = Math.floor(ms / 1000);
      const nanoseconds = Math.floor((ms % 1000) * 1e6);
      if (!time) return [seconds, nanoseconds];
      let diffSeconds = seconds - time[0];
      let diffNanoseconds = nanoseconds - time[1];
      if (diffNanoseconds < 0) {
        diffSeconds -= 1;
        diffNanoseconds += 1_000_000_000;
      }
      return [diffSeconds, diffNanoseconds];
    };
    (hrtimeShim as unknown as { bigint: () => bigint }).bigint = () =>
      BigInt(Math.floor(now * 1e6));
    (process as unknown as { hrtime: unknown }).hrtime = hrtimeShim;
  }

  // Virtual timer fns (cast away host setTimeout/Timeout typing — we return numeric ids).
  const vSetTimeout = ((cb: (...a: unknown[]) => void, ms = 0, ...args: unknown[]) => schedule('timeout', cb, ms, args)) as unknown;
  const vClear = ((id: number) => { pending.delete(id); }) as unknown;
  const vSetInterval = ((cb: (...a: unknown[]) => void, ms = 0, ...args: unknown[]) => schedule('interval', cb, ms, args, Math.max(0, Number(ms) || 0))) as unknown;
  // setImmediate(cb, ...args) → a near-zero-delay timeout that advances time by a
  // tiny SET_IMMEDIATE_MS so a busy-wait reschedule loop converges instead of
  // spinning forever at one virtual instant.
  const vSetImmediate = ((cb: (...a: unknown[]) => void, ...args: unknown[]) => schedule('timeout', cb, SET_IMMEDIATE_MS, args)) as unknown;
  const vRaf = ((cb: (t: number) => void) => schedule('raf', cb as (...a: unknown[]) => void, FRAME_MS, [])) as unknown;

  g['setTimeout'] = vSetTimeout;
  g['clearTimeout'] = vClear;
  g['setInterval'] = vSetInterval;
  g['clearInterval'] = vClear;
  g['setImmediate'] = vSetImmediate;
  g['clearImmediate'] = vClear;
  g['requestAnimationFrame'] = vRaf;
  g['cancelAnimationFrame'] = vClear;
  if (distinctWindow) {
    distinctWindow['requestAnimationFrame'] = vRaf;
    distinctWindow['cancelAnimationFrame'] = vClear;
    distinctWindow['setImmediate'] = vSetImmediate;
  }
  return { generation };
}

/**
 * The token of the clock installed now and of its current boot (`null` when none is installed): what a game booted
 * under it ticks with — taken right after the boot's `resetVirtualClock`.
 */
export function currentVirtualClock(): VirtualClockToken | null {
  return installed ? { generation, epoch } : null;
}

/** The REAL setTimeout/clearTimeout captured at install — for code that MUST run
 *  on the real wall clock even while virtual time is active (the per-test timeout
 *  guard `withTimeout`: a hung test must still fail, since the virtual clock only
 *  advances when the test driver ticks it). Returns the live globals when not
 *  installed. */
export function getRealSetTimeout(): typeof setTimeout {
  return (saved?.setTimeout ?? globalThis.setTimeout) as typeof setTimeout;
}
export function getRealClearTimeout(): typeof clearTimeout {
  return (saved?.clearTimeout ?? globalThis.clearTimeout) as typeof clearTimeout;
}
export function virtualTimersInstalled(): boolean {
  return installed;
}

/**
 * Restore the real globals. Safe to call when not installed. With `token`, only that install is restored: a superseded
 * one's is refused (returns false) — the clock is a later run's now. Without a token, whatever is installed is restored.
 * Returns whether it restored.
 */
export function restoreVirtualClock(token?: VirtualClockToken): boolean {
  if (!installed || !saved) return false;
  if (token && token.generation !== generation) return false;
  const g = globalThis as unknown as Record<string, unknown>;
  g['setTimeout'] = saved.setTimeout;
  g['clearTimeout'] = saved.clearTimeout;
  g['setInterval'] = saved.setInterval;
  g['clearInterval'] = saved.clearInterval;
  if (saved.setImmediate !== undefined) g['setImmediate'] = saved.setImmediate; else delete g['setImmediate'];
  if (saved.clearImmediate !== undefined) g['clearImmediate'] = saved.clearImmediate; else delete g['clearImmediate'];
  if (saved.requestAnimationFrame) g['requestAnimationFrame'] = saved.requestAnimationFrame; else delete g['requestAnimationFrame'];
  if (saved.cancelAnimationFrame) g['cancelAnimationFrame'] = saved.cancelAnimationFrame; else delete g['cancelAnimationFrame'];
  if (distinctWindow) {
    if (saved.winRaf !== undefined) distinctWindow['requestAnimationFrame'] = saved.winRaf; else delete distinctWindow['requestAnimationFrame'];
    if (saved.winCancelRaf !== undefined) distinctWindow['cancelAnimationFrame'] = saved.winCancelRaf; else delete distinctWindow['cancelAnimationFrame'];
    if (saved.winSetImmediate !== undefined) distinctWindow['setImmediate'] = saved.winSetImmediate; else delete distinctWindow['setImmediate'];
  }
  Math.random = saved.mathRandom;
  Date.now = saved.dateNow;
  if (saved.perfNow && typeof performance !== 'undefined') setPerformanceNow(saved.perfNow);
  if (saved.processHrtime && typeof process !== 'undefined') {
    (process as unknown as { hrtime: unknown }).hrtime = saved.processHrtime;
  }
  saved = null;
  installed = false;
  pending.clear();
  return true;
}

/** Current virtual time (ms since install). */
export function virtualNow(): number {
  return now;
}

/** Reset the clock to t=0 and DROP all pending timers, without re-saving/replacing
 *  globals. Call between per-test boots so a previous game's still-queued loop
 *  timers (rAF/setImmediate that didn't get cancelled) can't leak into the next
 *  test's advance() and so virtual time starts fresh each boot. It also starts a
 *  new BOOT: a token taken before it (a previous test's game, still going after
 *  its test timed out) can no longer `advance` this boot's clock. No-op if not
 *  installed. */
export function resetVirtualClock(): void {
  if (!installed) return;
  epoch += 1;
  now = 0;
  seq = 0;
  pending.clear();
}

/** Number of pending timers (diagnostics). */
export function pendingTimerCount(): number {
  return pending.size;
}

/**
 * Advance virtual time by `ms`, firing every timer that comes due in
 * `(now, now+ms]` in (due, seq) order, flushing microtasks between firings, then
 * setting `now = now+ms`. Timers scheduled by a fired callback (e.g. the game
 * loop rescheduling rAF, or the transport queueing a latency-delayed delivery)
 * are picked up within the same advance if they fall in the window.
 */
export async function advance(ms: number, token?: VirtualClockToken): Promise<void> {
  if (!installed) throw new Error('advance() called before installVirtualClock()');
  // The install this advance drives: the caller's, else the current one. A later install supersedes it — before the
  // call, or mid-advance (from a fired callback, or a run that started while this one awaited its microtasks) — and
  // from then on this advance fires nothing: those timers, and that time, are the later run's. The same for the boot a
  // game's token names: a later boot's reset ends it (a game that outlived its test, in the same run).
  const owner = token?.generation ?? generation;
  const boot = token?.epoch;
  const refuse = (when: string): void => {
    if (owner !== generation) {
      throw new Error(`advance(): the virtual clock it drives (install ${owner}) was superseded by a later install (${generation}) ${when} — a run that outlived its test cannot drive the next run's clock`);
    }
    if (boot !== undefined && boot !== epoch) {
      throw new Error(`advance(): the boot it drives (${boot}) was reset by a later boot (${epoch}) ${when} — a game that outlived its test cannot drive the next test's clock`);
    }
  };
  refuse('before the call');
  const target = now + Math.max(0, ms);
  let firingsAtInstant = 0;
  let lastFiredDue = -1;
  for (;;) {
    refuse('during it');
    const t = earliest(target);
    if (!t) break;
    if (t.due === lastFiredDue) {
      if (++firingsAtInstant > MAX_FIRINGS_AT_INSTANT) {
        throw new Error(`virtualClock.advance: ${MAX_FIRINGS_AT_INSTANT}+ timers fired at virtual t=${t.due}ms — a timer is rescheduling itself with no delay`);
      }
    } else {
      firingsAtInstant = 0;
      lastFiredDue = t.due;
    }
    now = t.due;
    if (t.kind === 'interval' && t.interval !== undefined) {
      t.due = now + Math.max(1, t.interval); // re-arm; min 1ms so it can't spin
    } else {
      pending.delete(t.id);
    }
    try {
      if (t.kind === 'raf') t.cb(now);
      else t.cb(...t.args);
    } catch (err) {
      // A throwing timer callback must not wedge the clock; surface + continue,
      // matching how the real event loop isolates a thrown timer handler.
      // eslint-disable-next-line no-console
      console.error('[virtualClock] timer callback threw:', err);
    }
    // Let awaited Promises chained off this callback resolve before the next timer.
    await flushMicrotasks();
  }
  refuse('during it');
  now = target;
}

/** Flush only pending microtasks (no timer firing). Used right after the boot's
 *  synchronous `await`s to settle promise chains before the first tick. */
export async function settleMicrotasks(times = 3): Promise<void> {
  for (let i = 0; i < times; i++) await flushMicrotasks();
}
